import React, {
    createContext,
    useContext,
    useCallback,
    useRef,
    useState,
    useEffect,
    useLayoutEffect
} from "react";
import { apiDirectCall } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";

interface IAvatarData {
    name: string;
    photo: string | null;
}

interface IAvatarCache {
    [applicantId: string]: IAvatarData | null;
}

interface IBatchAvatarContext {
    getAvatar: (applicantId: string) => IAvatarData | null | undefined;
    requestAvatar: (applicantId: string) => void;
    isLoading: (applicantId: string) => boolean;
}

const BatchAvatarContext = createContext<IBatchAvatarContext | undefined>(
    undefined
);

const BATCH_SIZE = 10;
const DEBOUNCE_MS = 50;

interface IBatchAvatarProviderProps {
    children: React.ReactNode;
}

export const BatchAvatarProvider: React.FC<IBatchAvatarProviderProps> = ({
    children
}) => {
    const { company } = useAuthState();
    const [cache, setCache] = useState<IAvatarCache>({});
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
    const pendingIdsRef = useRef<Set<string>>(new Set());
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isDrainingRef = useRef(false);
    const isMountedRef = useRef(true);

    const fetchBatch = useCallback(
        async (ids: string[]) => {
            if (ids.length === 0) return;

            // Mark as loading
            setLoadingIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return next;
            });

            try {
                const response = await apiDirectCall(
                    "/applicant/getavatarapplicantbatch",
                    "post",
                    {
                        companyId: company.companyId,
                        applicantIds: ids
                    }
                );

                const avatars = response?.data?.avatars || {};
                if (!isMountedRef.current) {
                    return;
                }

                // Update cache with results
                setCache((prev) => {
                    const next = { ...prev };
                    ids.forEach((id) => {
                        next[id] = avatars[id] || null;
                    });
                    return next;
                });
            } catch (error) {
                if (!isMountedRef.current) {
                    return;
                }
                console.error("Error fetching batch avatars:", error);
                // Mark failed ones as null in cache
                setCache((prev) => {
                    const next = { ...prev };
                    ids.forEach((id) => {
                        if (next[id] === undefined) {
                            next[id] = null;
                        }
                    });
                    return next;
                });
            } finally {
                if (!isMountedRef.current) {
                    return;
                }
                // Remove from loading
                setLoadingIds((prev) => {
                    const next = new Set(prev);
                    ids.forEach((id) => next.delete(id));
                    return next;
                });
            }
        },
        [company.companyId]
    );

    const processPendingRequests = useCallback(async () => {
        if (!isMountedRef.current || isDrainingRef.current) {
            return;
        }

        isDrainingRef.current = true;
        try {
            while (isMountedRef.current && pendingIdsRef.current.size > 0) {
                const batch = Array.from(pendingIdsRef.current).slice(
                    0,
                    BATCH_SIZE
                );
                batch.forEach(id => pendingIdsRef.current.delete(id));
                await fetchBatch(batch);
            }
        } finally {
            isDrainingRef.current = false;
        }
    }, [fetchBatch]);

    const requestAvatar = useCallback(
        (applicantId: string) => {
            // Skip if already cached or loading
            if (
                cache[applicantId] !== undefined ||
                loadingIds.has(applicantId)
            ) {
                return;
            }

            // Add to pending
            pendingIdsRef.current.add(applicantId);

            // Debounce the batch request
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(
                processPendingRequests,
                DEBOUNCE_MS
            );
        },
        [cache, loadingIds, processPendingRequests]
    );

    const getAvatar = useCallback(
        (applicantId: string): IAvatarData | null | undefined => {
            return cache[applicantId];
        },
        [cache]
    );

    const isLoading = useCallback(
        (applicantId: string): boolean => {
            return loadingIds.has(applicantId);
        },
        [loadingIds]
    );

    // Cleanup on unmount
    useLayoutEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            pendingIdsRef.current.clear();
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const contextValue: IBatchAvatarContext = {
        getAvatar,
        requestAvatar,
        isLoading
    };

    return (
        <BatchAvatarContext.Provider value={contextValue}>
            {children}
        </BatchAvatarContext.Provider>
    );
};

export const useBatchAvatar = (applicantId: string) => {
    const context = useContext(BatchAvatarContext);

    // Request avatar on mount/id change (must be before any conditional returns)
    useEffect(() => {
        if (context && applicantId) {
            context.requestAvatar(applicantId);
        }
    }, [applicantId, context]);

    if (!context) {
        // Fallback: return undefined values if not wrapped in provider
        return {
            avatar: undefined as IAvatarData | null | undefined,
            isLoading: false,
            isAvailable: false
        };
    }

    return {
        avatar: context.getAvatar(applicantId),
        isLoading: context.isLoading(applicantId),
        isAvailable: true
    };
};

export default useBatchAvatar;
